import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { 
  StarIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils';
import { sellerService, SellerProfile } from '../services/sellerService';
import { Product } from '../services/productService';
import { CATEGORY_LABELS, SECTOR_LABELS, ProductCategory, Sector } from '../types';

export const SellerProfilePage: React.FC = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<SellerProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!sellerId) return;

    const loadProfile = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await sellerService.getSellerProfile(sellerId);
        setProfile(data);
      } catch (err: any) {
        console.error('Error loading seller profile:', err);
        setError(err.response?.data?.message || 'Failed to load seller profile. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    loadProfile();
  }, [sellerId]);

  const getImageUrl = (product: Product): string => {
    if (product.thumbnailUrl) return product.thumbnailUrl;
    if (product.thumbnail) {
      const thumbnailMedia = product.media?.find(m => m.entityType === 'PRODUCT_THUMBNAIL');
      if (thumbnailMedia?.signedUrl) return thumbnailMedia.signedUrl;
      if (thumbnailMedia?.fileUrl) return thumbnailMedia.fileUrl;
    }
    if (product.media && product.media.length > 0) {
      const firstMedia = product.media[0];
      if (firstMedia.signedUrl) return firstMedia.signedUrl;
      if (firstMedia.fileUrl) return firstMedia.fileUrl;
    }
    return 'https://via.placeholder.com/300x200?text=No+Image';
  };

  const getInitials = (name: string | null): string => {
    if (!name) return 'U';
    const parts = name.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-secondary-600">Loading seller profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-secondary-900 mb-4">
            {error || 'Seller Not Found'}
          </h1>
          <p className="text-secondary-600 mb-8">
            {error || 'The seller you\'re looking for doesn\'t exist or has been removed.'}
          </p>
          <button
            onClick={() => navigate('/marketplace')}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Browse Marketplace
          </button>
        </div>
      </div>
    );
  }

  const { seller, products, reviews, stats } = profile;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-secondary-600 hover:text-primary-600 mb-6 transition-colors"
      >
        <ArrowLeftIcon className="h-5 w-5 mr-1" />
        Back
      </button>

      {/* Seller Header */}
      <Card className="mb-8">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              {seller.avatar ? (
                <img
                  src={seller.avatar}
                  alt={seller.name || 'Seller'}
                  className="h-24 w-24 rounded-full object-cover border-4 border-primary-100"
                />
              ) : (
                <div className="h-24 w-24 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center border-4 border-primary-100">
                  <span className="text-3xl font-bold text-white">
                    {getInitials(seller.name)}
                  </span>
                </div>
              )}
            </div>

            {/* Seller Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-3xl font-bold text-secondary-900">
                  {seller.name || 'Unknown Seller'}
                </h1>
                {seller.isEmailVerified && (
                  <CheckCircleIcon className="h-6 w-6 text-green-500" title="Verified Seller" />
                )}
              </div>
              <p className="text-secondary-600 mb-4">{seller.email}</p>

              {/* Stats */}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center">
                  <StarIcon className="h-5 w-5 text-yellow-400 fill-current mr-1" />
                  <span className="font-medium">
                    {stats.averageRating > 0 ? stats.averageRating.toFixed(1) : '0.0'}
                  </span>
                  <span className="ml-1 text-secondary-600">
                    ({stats.totalReviews} {stats.totalReviews === 1 ? 'review' : 'reviews'})
                  </span>
                </div>
                <div className="text-secondary-600">
                  <span className="font-medium">{stats.totalProducts}</span> products
                </div>
                <div className="text-secondary-600">
                  <span className="font-medium">{stats.totalSales}</span> sales
                </div>
                <div className="text-secondary-600">
                  Member since {new Date(seller.createdAt).getFullYear()}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Products Section */}
      <div className="mb-12">
        <h2 className="text-2xl font-bold text-secondary-900 mb-6">
          Products ({products.length})
        </h2>
        {products.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-secondary-600">This seller hasn't listed any products yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.id}`}
                className="group"
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-gradient-to-br from-primary-50 to-primary-100 overflow-hidden rounded-t-lg">
                    <img
                      src={getImageUrl(product)}
                      alt={product.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image';
                      }}
                    />
                  </div>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge variant="primary" className="text-xs">
                        {CATEGORY_LABELS[product.category as ProductCategory] || product.category}
                      </Badge>
                      <Badge variant="secondary" className="text-xs">
                        {SECTOR_LABELS[product.sector as Sector] || product.sector}
                      </Badge>
                    </div>
                    <h3 className="font-semibold text-secondary-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {product.title}
                    </h3>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <StarIcon className="h-4 w-4 text-yellow-400 fill-current" />
                        <span className="text-sm font-medium">
                          {product.rating > 0 ? product.rating.toFixed(1) : '0.0'}
                        </span>
                        <span className="text-xs text-secondary-500 ml-1">
                          ({product.reviewCount || product._count?.reviews || 0})
                        </span>
                      </div>
                      <span className="text-lg font-bold text-primary-600">
                        {formatCurrency(parseFloat(product.price))}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Reviews Section */}
      <div>
        <h2 className="text-2xl font-bold text-secondary-900 mb-6">
          Reviews ({reviews.length})
        </h2>
        {reviews.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-secondary-600">No reviews yet for this seller's products.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <Card key={review.id}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Reviewer Avatar */}
                    <div className="flex-shrink-0">
                      {review.user?.avatar ? (
                        <img
                          src={review.user.avatar}
                          alt={review.user.name || 'Reviewer'}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
                          <span className="text-sm font-bold text-white">
                            {getInitials(review.user?.name)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Review Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="font-semibold text-secondary-900">
                            {review.user?.name || 'Anonymous'}
                          </p>
                          <div className="flex items-center gap-1 mt-1">
                            {[...Array(5)].map((_, i) => (
                              <StarIcon
                                key={i}
                                className={`h-4 w-4 ${
                                  i < review.rating
                                    ? 'text-yellow-400 fill-current'
                                    : 'text-secondary-300'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <span className="text-sm text-secondary-500">
                          {new Date(review.createdAt).toLocaleDateString()}
                        </span>
                      </div>

                      {review.product && (
                        <Link
                          to={`/product/${review.product.id}`}
                          className="text-sm text-primary-600 hover:text-primary-700 mb-2 inline-block"
                        >
                          Review for: {review.product.title}
                        </Link>
                      )}

                      {review.comment && (
                        <p className="text-secondary-700 mt-2 whitespace-pre-line">
                          {review.comment}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

